'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { events, categories } from '@/lib/mockData';
import AppImage from '@/components/ui/AppImage';
import StatusBadge from '@/components/ui/StatusBadge';
import { Search, SlidersHorizontal, CalendarDays, MapPin, Users, X, ChevronDown } from 'lucide-react';

function formatDate(dateStr: string) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [y, m, d] = dateStr.split('-');
  return `${months[parseInt(m) - 1]} ${parseInt(d)}, ${y}`;
}

const sortOptions = [
  { value: 'date-asc', label: 'Date: Soonest first' },
  { value: 'date-desc', label: 'Date: Latest first' },
  { value: 'price-asc', label: 'Price: Low to high' },
  { value: 'price-desc', label: 'Price: High to low' },
  { value: 'seats', label: 'Seats: Most available' },
];

export default function EventCatalogContent() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState('date-asc');
  const [showFilters, setShowFilters] = useState(false);
  const [priceMax, setPriceMax] = useState(1000);

  const filtered = useMemo(() => {
    let result = [...events];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.city.toLowerCase().includes(q) ||
          e.organizerName.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q)
      );
    }
    if (activeCategory !== 'all') {
      result = result.filter((e) => e.categoryId === activeCategory);
    }
    if (activeStatus !== 'all') {
      result = result.filter((e) => e.status === activeStatus);
    }
    result = result.filter((e) => e.ticketPrice <= priceMax);

    switch (sortBy) {
      case 'date-asc': result.sort((a, b) => a.date.localeCompare(b.date)); break;
      case 'date-desc': result.sort((a, b) => b.date.localeCompare(a.date)); break;
      case 'price-asc': result.sort((a, b) => a.ticketPrice - b.ticketPrice); break;
      case 'price-desc': result.sort((a, b) => b.ticketPrice - a.ticketPrice); break;
      case 'seats': result.sort((a, b) => b.seatsRemaining - a.seatsRemaining); break;
    }
    return result;
  }, [search, activeCategory, activeStatus, sortBy, priceMax]);

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-foreground mb-1">Browse Events</h1>
        <p className="text-muted-foreground text-sm">{filtered.length} events found</p>
      </div>

      {/* Search + Sort bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-3">
          <Search size={17} className="text-muted-foreground shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events, venues, cities, or organizers..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground transition-colors">
              <X size={15} />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none bg-card border border-border rounded-xl px-4 py-3 pr-9 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
            >
              {sortOptions.map((opt) => (
                <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-150 ${
              showFilters ? 'bg-secondary border-primary text-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
            }`}
          >
            <SlidersHorizontal size={16} />
            Filters
          </button>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-150 ${
            activeCategory === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
          }`}
        >
          All Categories
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(activeCategory === cat.id ? 'all' : cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-150 ${
              activeCategory === cat.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="mb-6 p-5 bg-card border border-border rounded-2xl flex flex-wrap gap-6 fade-in">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Status</p>
            <div className="flex gap-2 flex-wrap">
              {['all', 'published', 'completed', 'cancelled'].map((s) => (
                <button
                  key={`status-filter-${s}`}
                  onClick={() => setActiveStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 ${
                    activeStatus === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              Max Price: <span className="text-foreground">${priceMax}</span>
            </p>
            <input
              type="range"
              min={0}
              max={1000}
              step={10}
              value={priceMax}
              onChange={(e) => setPriceMax(Number(e.target.value))}
              className="w-48 accent-primary"
            />
          </div>
          <button
            onClick={() => { setActiveStatus('all'); setPriceMax(1000); setActiveCategory('all'); setSearch(''); }}
            className="self-end px-4 py-2 text-xs font-semibold text-danger border border-danger/30 bg-danger-bg rounded-lg hover:opacity-80 transition-all"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Event grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <CalendarDays size={28} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">No events found</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
            Try adjusting your search or filters. New events are added weekly.
          </p>
          <button
            onClick={() => { setSearch(''); setActiveCategory('all'); setActiveStatus('all'); setPriceMax(1000); }}
            className="px-5 py-2.5 bg-primary text-primary-foreground font-semibold text-sm rounded-xl hover:opacity-90 active:scale-95 transition-all"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((event) => (
            <Link key={event.id} href="/event-detail" className="group block bg-card rounded-2xl border border-border overflow-hidden card-hover shadow-card">
              <div className="relative h-44 overflow-hidden">
                <AppImage
                  src={event.imageUrl}
                  alt={`${event.title} event at ${event.venue} in ${event.city}`}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                />
                <div className="absolute top-2 left-2">
                  <span className="text-xs font-semibold px-2.5 py-1 bg-white/90 backdrop-blur-sm text-primary rounded-full">
                    {event.category}
                  </span>
                </div>
                <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                  <StatusBadge status={event.status} />
                  {event.seatsRemaining === 0 && (
                    <span className="text-xs font-semibold px-2 py-0.5 bg-danger text-white rounded-full">Sold Out</span>
                  )}
                  {event.seatsRemaining > 0 && event.seatsRemaining < 15 && (
                    <span className="text-xs font-semibold px-2 py-0.5 bg-warning text-white rounded-full">
                      {event.seatsRemaining} left!
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-foreground text-sm mb-0.5 line-clamp-2 group-hover:text-primary transition-colors leading-snug">
                  {event.title}
                </h3>
                <p className="text-xs text-muted-foreground mb-3">{event.organizerName}</p>
                <div className="flex flex-col gap-1 mb-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarDays size={12} />
                    <span>{formatDate(event.date)} · {event.time}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin size={12} />
                    <span className="truncate">{event.city}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users size={12} />
                    <span>{event.seatsRemaining > 0 ? `${event.seatsRemaining} seats remaining` : 'Sold out'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div>
                    <span className="text-lg font-extrabold text-foreground tabular-nums">${event.ticketPrice}</span>
                    {event.vipPrice && <span className="text-xs text-muted-foreground ml-1">/ VIP ${event.vipPrice}</span>}
                  </div>
                  {event.seatsRemaining > 0 ? (
                    <span className="text-xs font-semibold text-primary bg-secondary px-3 py-1.5 rounded-lg group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-150">
                      Register →
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-muted-foreground bg-muted px-3 py-1.5 rounded-lg">
                      Sold Out
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}